import { Tabs as T } from 'radix-ui';
import { ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';
import { NavLink, matchPath, useLocation, useNavigate } from 'react-router';
import { cn } from '@/lib/cn';
import { Menu, MenuItem } from './menu';

const tabClass =
  'relative inline-flex h-9 items-center gap-1.5 whitespace-nowrap px-1 text-sm font-medium text-muted transition-colors hover:text-fg data-[state=active]:text-fg data-[state=active]:after:absolute data-[state=active]:after:inset-x-0 data-[state=active]:after:-bottom-px data-[state=active]:after:h-0.5 data-[state=active]:after:bg-accent [&_svg]:size-4';
const activeClass = 'text-fg after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:bg-accent';

const Count = ({ n }: { n?: number }) => (n !== undefined ? <span className="rounded-full bg-surface-hover px-1.5 text-xs text-muted">{n}</span> : null);

/**
 * Tabs that share a `group` collapse into one dropdown tab, so a page with many tabs keeps a
 * single row. The group sits where its first tab would be, and shows the active tab's name.
 */
type Slot<Tab> = { kind: 'tab'; tab: Tab } | { kind: 'group'; name: string; tabs: Tab[] };
function slots<Tab extends { group?: string }>(tabs: Tab[]): Slot<Tab>[] {
  const out: Slot<Tab>[] = [];
  const groups = new Map<string, Tab[]>();
  for (const tab of tabs) {
    if (!tab.group) {
      out.push({ kind: 'tab', tab });
      continue;
    }
    const existing = groups.get(tab.group);
    if (existing) existing.push(tab);
    else {
      const list = [tab];
      groups.set(tab.group, list);
      out.push({ kind: 'group', name: tab.group, tabs: list });
    }
  }
  return out;
}

function GroupTrigger({ name, active, children }: { name: string; active?: ReactNode; children: ReactNode }) {
  return (
    <Menu
      align="start"
      trigger={
        <button type="button" className={cn(tabClass, active && activeClass)}>
          {active ? (
            <>
              <span className="text-subtle">{name}:</span> {active}
            </>
          ) : (
            name
          )}
          <ChevronDown />
        </button>
      }
    >
      {children}
    </Menu>
  );
}

type InPageTab = { value: string; label: ReactNode; count?: number; group?: string };

/** In-page tabs. For tabs that are routes, use TabNav. */
export function Tabs({ tabs, value, onValueChange, children }: { tabs: InPageTab[]; value: string; onValueChange: (v: string) => void; children: ReactNode }) {
  return (
    <T.Root value={value} onValueChange={onValueChange}>
      <T.List className="mb-4 flex gap-5 overflow-x-auto border-b">
        {slots(tabs).map((s) =>
          s.kind === 'tab' ? (
            <T.Trigger key={s.tab.value} value={s.tab.value} className={tabClass}>
              {s.tab.label}
              <Count n={s.tab.count} />
            </T.Trigger>
          ) : (
            <GroupTrigger key={`group:${s.name}`} name={s.name} active={s.tabs.find((t) => t.value === value)?.label}>
              {s.tabs.map((t) => (
                <MenuItem key={t.value} onSelect={() => onValueChange(t.value)}>
                  {t.label}
                  <Count n={t.count} />
                </MenuItem>
              ))}
            </GroupTrigger>
          ),
        )}
      </T.List>
      {children}
    </T.Root>
  );
}
export const TabPanel = T.Content;

type RouteTab = { to: string; label: ReactNode; end?: boolean; count?: number; group?: string };

/** Route-driven tabs: each tab is a link and the active one follows the URL. */
export function TabNav({ tabs, className }: { tabs: RouteTab[]; className?: string }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isActive = (t: RouteTab) => Boolean(matchPath({ path: t.to, end: t.end ?? false }, pathname));
  return (
    <nav className={cn('mb-4 flex gap-5 overflow-x-auto border-b', className)}>
      {slots(tabs).map((s) =>
        s.kind === 'tab' ? (
          <NavLink key={s.tab.to} to={s.tab.to} end={s.tab.end} className={({ isActive: on }) => cn(tabClass, on && activeClass)}>
            {s.tab.label}
            <Count n={s.tab.count} />
          </NavLink>
        ) : (
          <GroupTrigger key={`group:${s.name}`} name={s.name} active={s.tabs.find(isActive)?.label}>
            {s.tabs.map((t) => (
              <MenuItem key={t.to} onSelect={() => void navigate(t.to)}>
                {t.label}
                <Count n={t.count} />
              </MenuItem>
            ))}
          </GroupTrigger>
        ),
      )}
    </nav>
  );
}
